import { useState, useEffect } from 'react';
import { getActiveStaff } from '../../firebase/staffService';

function SkinRecordForm({ clientId, clientName, existingRecord, onSave, onCancel, isClient = false }) {
  const [formData, setFormData] = useState({
    dateOfBirth: '',
    address: '',
    skinDescription: '',
    skinSensitivity: '',
    fitzpatrickType: '',
    fitzpatrickDescription: '',
    skinConditions: [],
    skinMedications: '',
    skinAllergies: '',
    recentTreatments: [],
    pregnantOrLactating: false,
    skinRegime: '',
    productsUsed: '',
    notes: '',
    skinTherapistName: isClient ? 'Client Self-Signed' : '',
  });
  
  const [staffList, setStaffList] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isClient) {
      loadStaff();
    }
  }, []);

  useEffect(() => {
    if (existingRecord) {
      setFormData(existingRecord);
    }
  }, [existingRecord]);

  const loadStaff = async () => {
    const allStaff = await getActiveStaff();
    const skinStaff = allStaff.filter(s => s.type === 'Skin Aesthetician');
    setStaffList(skinStaff);
  };

  const handleArrayToggle = (arrayName, value) => {
    setFormData(prev => {
      const current = prev[arrayName] || [];
      if (current.includes(value)) {
        return { ...prev, [arrayName]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [arrayName]: [...current, value] };
      }
    });
  };

  const handleTextChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSelectChange = (e) => {
    setFormData(prev => ({ ...prev, skinTherapistName: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isClient && !formData.skinTherapistName) {
      alert('Please select a skin therapist');
      return;
    }
    setSaving(true);
    await onSave(formData, formData.skinTherapistName);
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Personal Info */}
      <div className="bg-white rounded-lg border p-4">
        <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
            <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleTextChange} className="mt-1 w-full px-3 py-2 border rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Address</label>
            <input type="text" name="address" value={formData.address} onChange={handleTextChange} className="mt-1 w-full px-3 py-2 border rounded-md" />
          </div>
        </div>
      </div>
      
      {/* Skin Assessment */}
      <div className="bg-white rounded-lg border p-4">
        <h3 className="text-lg font-semibold mb-4">Skin Assessment</h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Skin Description</label>
            <select name="skinDescription" value={formData.skinDescription} onChange={handleTextChange} className="mt-1 w-full px-3 py-2 border rounded-md">
              <option value="">Select</option>
              <option value="Dry">Dry</option>
              <option value="Normal">Normal</option>
              <option value="Oily">Oily</option>
              <option value="Combination">Combination</option>
              <option value="Dehydrated">Dehydrated</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Skin Sensitivity</label>
            <select name="skinSensitivity" value={formData.skinSensitivity} onChange={handleTextChange} className="mt-1 w-full px-3 py-2 border rounded-md">
              <option value="">Select</option>
              <option value="Normal">Normal</option>
              <option value="Sensitive">Sensitive</option>
              <option value="Very Sensitive">Very Sensitive</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Fitzpatrick Skin Type</label>
            <select name="fitzpatrickType" value={formData.fitzpatrickType} onChange={handleTextChange} className="mt-1 w-full px-3 py-2 border rounded-md">
              <option value="">Select</option>
              <option value="Burn easily">Burn easily</option>
              <option value="Mild Tan">Mild Tan</option>
              <option value="Tan Easily">Tan Easily</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Skin Conditions</label>
            <div className="space-y-1">
              {['Acne', 'Large pores', 'Skin texture - Elasticity', 'Hyper pigmentation', 'Rosacea', 'Deep lines', 'Melasma'].map(condition => (
                <label key={condition} className="flex items-center gap-2">
                  <input type="checkbox" checked={(formData.skinConditions || []).includes(condition)} onChange={() => handleArrayToggle('skinConditions', condition)} className="h-4 w-4" />
                  <span className="text-sm">{condition}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Are you taking any medication?</label>
          <textarea name="skinMedications" value={formData.skinMedications} onChange={handleTextChange} rows={2} className="mt-1 w-full px-3 py-2 border rounded-md" placeholder="Please list any medications"></textarea>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Do you have any allergies?</label>
          <textarea name="skinAllergies" value={formData.skinAllergies} onChange={handleTextChange} rows={2} className="mt-1 w-full px-3 py-2 border rounded-md" placeholder="Please list any allergies"></textarea>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Have you had any of the following treatments in the last 6 weeks?</label>
          <div className="space-y-1">
            {['Botox', 'Laser/IPL', 'Moles, skintag or sunspot removal', 'Face peels'].map(treatment => (
              <label key={treatment} className="flex items-center gap-2">
                <input type="checkbox" checked={(formData.recentTreatments || []).includes(treatment)} onChange={() => handleArrayToggle('recentTreatments', treatment)} className="h-4 w-4" />
                <span className="text-sm">{treatment}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4 mb-4">
          <span className="w-48">Are you currently pregnant or lactating?</span>
          <button type="button" onClick={() => setFormData(prev => ({ ...prev, pregnantOrLactating: true }))} className={`px-4 py-1 rounded ${formData.pregnantOrLactating === true ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>Yes</button>
          <button type="button" onClick={() => setFormData(prev => ({ ...prev, pregnantOrLactating: false }))} className={`px-4 py-1 rounded ${formData.pregnantOrLactating === false ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>No</button>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">What is your current skin regime & what products do you use?</label>
          <textarea name="skinRegime" value={formData.skinRegime} onChange={handleTextChange} rows={3} className="mt-1 w-full px-3 py-2 border rounded-md" placeholder="Describe your daily routine and products"></textarea>
        </div>
      </div>

      {/* Notes Section */}
      <div className="bg-white rounded-lg border p-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes / Extra Remarks
        </label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleTextChange}
          rows={3}
          className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-salon-button"
          placeholder="Any additional notes about the client's skin concerns, preferences, or special instructions..."
        />
        <p className="text-xs text-gray-400 mt-1">Optional – for internal use only</p>
      </div>

      {/* Therapist selection - only show for staff */}
      {!isClient && (
        <div className="bg-white rounded-lg border p-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Skin Therapist Name</label>
          <select 
            value={formData.skinTherapistName} 
            onChange={handleSelectChange}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-salon-button"
            required
          >
            <option value="">Select therapist</option>
            {staffList.map(staff => (
              <option key={staff.id} value={staff.name}>{staff.name} ({staff.type})</option>
            ))}
          </select>
        </div>
      )}
      
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="px-4 py-2 bg-salon-button text-white rounded-md hover:bg-salon-button-hover disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Skin Record'}
        </button>
      </div>
    </form>
  );
}

export default SkinRecordForm;